// ─────────────────────────────────────────────────────────────
// src/utils/taxCalculations.js
// Pure functions for Egyptian income-tax / salary calculations.
// No side-effects, no React, fully unit-testable.
// ─────────────────────────────────────────────────────────────

/**
 * Supplemental tax look-up for annual taxable income
 * between 600 001 and 1 200 000 EGP (Egyptian tax law).
 * @param {number} annualTaxableIncome
 * @returns {number}
 */
const getSurtaxLookup = (annualTaxableIncome) => {
  if (annualTaxableIncome > 600_000 && annualTaxableIncome <= 700_000) return 4_000;
  if (annualTaxableIncome > 700_000 && annualTaxableIncome <= 800_000) return 6_750;
  if (annualTaxableIncome > 800_000 && annualTaxableIncome <= 900_000) return 10_250;
  if (annualTaxableIncome > 900_000)                                   return 15_250;
  return 0;
};

/**
 * Compute Egyptian annual income tax from the annual taxable income.
 * @param {number} annualTaxableIncome  – income after the 20 000 EGP exemption
 * @returns {number} annual tax amount
 */
const computeAnnualTax = (annualTaxableIncome) => {
  if (annualTaxableIncome <= 40_000)    return 0;
  if (annualTaxableIncome <= 55_000)    return (annualTaxableIncome - 40_000) * 0.10;
  if (annualTaxableIncome <= 70_000)    return (annualTaxableIncome - 55_000) * 0.15 + 1_500;
  if (annualTaxableIncome <= 200_000)   return (annualTaxableIncome - 70_000) * 0.20 + 3_750;
  if (annualTaxableIncome <= 400_000)   return (annualTaxableIncome - 200_000) * 0.225 + 29_750;
  if (annualTaxableIncome <= 600_000)   return (annualTaxableIncome - 400_000) * 0.25 + 74_750;
  if (annualTaxableIncome <= 1_200_000) return (annualTaxableIncome - 400_000) * 0.25 + 74_750 + getSurtaxLookup(annualTaxableIncome);
  // Above 1 200 000
  return (annualTaxableIncome - 1_200_000) * 0.275 + 300_000;
};

/**
 * Calculate net salary and all deductions given a gross salary.
 *
 * @param {number} gross       – monthly gross salary (EGP)
 * @param {number} insSalary   – insurance base salary (EGP)
 * @returns {{
 *   netSalary: number,
 *   grossSalary: number,
 *   monthlyTax: number,
 *   yearlyTax: number,
 *   martyrsFund: number,
 *   empShare: number
 * }}
 */
export const calculateNetFromGross = (gross, insSalary) => {
  const empShare             = insSalary * 0.11;
  const netBeforeTax         = gross - empShare;
  const annualBeforeTax      = netBeforeTax * 12;

  const annualTaxableIncome  = annualBeforeTax <= 20_000
    ? 0
    : annualBeforeTax - 20_000;

  const yearlyTax   = computeAnnualTax(annualTaxableIncome);
  const monthlyTax  = yearlyTax / 12;
  const martyrsFund = gross * 0.0005;
  const netSalary   = netBeforeTax - (monthlyTax + martyrsFund);

  return { netSalary, grossSalary: gross, monthlyTax, yearlyTax, martyrsFund, empShare };
};

/**
 * Binary-search for the gross salary that yields a given net salary.
 *
 * @param {number} targetNet  – desired monthly net salary (EGP)
 * @param {number} insSalary  – insurance base salary (EGP)
 * @returns {number}          – required gross salary
 */
export const calculateGrossFromNet = (targetNet, insSalary) => {
  let low  = targetNet;
  let high = targetNet * 3;

  const TOLERANCE      = 0.0001;
  const MAX_ITERATIONS = 200;

  for (let i = 0; i < MAX_ITERATIONS; i++) {
    const mid        = (low + high) / 2;
    const { netSalary } = calculateNetFromGross(mid, insSalary);
    const diff       = netSalary - targetNet;

    if (Math.abs(diff) < TOLERANCE) return mid;
    if (netSalary < targetNet) low = mid;
    else high = mid;
  }

  return (low + high) / 2;
};

/**
 * Validate individual-mode form inputs.
 *
 * @param {{ gross: string, net: string, insurance: string }} inputs
 * @param {'netFromGross'|'grossFromNet'} mode
 * @param {object} t  – translation strings
 * @returns {object}  – error map (empty = valid)
 */
export const validateSalaryInputs = (inputs, mode, t) => {
  const errors = {};

  if (!inputs.insurance || parseFloat(inputs.insurance) <= 0) {
    errors.insurance = t.insuranceSalaryError;
  }
  if (mode === 'netFromGross' && (!inputs.gross || parseFloat(inputs.gross) <= 0)) {
    errors.gross = t.grossSalaryError;
  }
  if (mode === 'grossFromNet' && (!inputs.net || parseFloat(inputs.net) <= 0)) {
    errors.net = t.netSalaryError;
  }

  return errors;
};